import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import CircularProgress from '@mui/material/CircularProgress';
import { getPerformanceReviewEmployee } from '../api/performanceReviewApi';
import PerformanceReviewManagerModal from './PerformanceReviewManagerModal';

export default function PerformanceReviewManagerPanel({ employeeUserId, employeeName, year, quarter }) {
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['performance-review-employee', employeeUserId, year, quarter],
    queryFn: () => getPerformanceReviewEmployee(employeeUserId, { year, quarter }),
    enabled: !!employeeUserId && year != null && quarter != null,
  });

  const submission = data?.submission;
  const cycle = data?.cycle;
  const status = submission?.status;
  const hideManagerCalibrationFromViewer = data?.can_see_manager_calibration === false;

  if (!year || !quarter) {
    return <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No review cycle in context.</p>;
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '1rem' }}>
        <CircularProgress size={28} />
        <span style={{ color: '#64748b', fontSize: '0.85rem' }}>Loading review…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <p style={{ color: '#b91c1c', fontSize: '0.85rem' }}>
        {error?.response?.data?.message || 'Unable to load this performance review.'}
      </p>
    );
  }

  if (!cycle) {
    return <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No performance review cycle exists yet.</p>;
  }

  if (!submission) {
    return (
      <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0' }}>
        <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
          Formal review ({cycle.label || `${year} Q${quarter}`})
        </h4>
        <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>
          {employeeName || 'This person'} has not started a performance review for this period yet.
        </p>
      </div>
    );
  }

  const displayName = employeeName || 'This employee';

  if (status === 'draft') {
    return (
      <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0' }}>
        <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
          Formal review ({cycle.label || `${year} Q${quarter}`})
        </h4>
        <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
          Waiting for <strong>{displayName}</strong> to submit their performance review.
        </p>
      </div>
    );
  }

  const isPublished = status === 'published';
  const isRevisionPending = status === 'revision_requested';
  const buttonLabel = isPublished
    ? 'View published performance review'
    : isRevisionPending
      ? 'View submission (re-edit pending)'
      : 'Open performance review submission';

  return (
    <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0' }}>
      <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>
        Formal review ({cycle.label || `${year} Q${quarter}`})
      </h4>
      <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 0.85rem', lineHeight: 1.5 }}>
        {isPublished
          ? 'This review has been published. The employee can see their submission and your feedback.'
          : isRevisionPending
            ? 'Waiting for the employee to update and resubmit after your revision request.'
            : ['manager', 'product_manager'].includes(String(submission?.employee_role || '').toLowerCase())
              ? 'Submitted — for people managers this review uses executive calibration only; an admin or superadmin opens the form to add executive ratings and publish when ready.'
              : hideManagerCalibrationFromViewer
                ? 'Submitted — manager calibration is visible only to their assigned manager and leadership. You can open the form to view objectives and self-ratings.'
                : 'Submitted — open to add manager ratings and comments; an admin or superadmin must publish before the employee sees that feedback.'}
      </p>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        style={{
          padding: '0.55rem 1rem',
          borderRadius: 8,
          border: 'none',
          background: isPublished ? '#1272B3' : '#0f172a',
          color: '#fff',
          fontWeight: 700,
          fontSize: '0.85rem',
          cursor: 'pointer',
        }}
      >
        {buttonLabel}
      </button>

      <PerformanceReviewManagerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        employeeUserId={employeeUserId}
        employeeName={employeeName}
        year={year}
        quarter={quarter}
      />
    </div>
  );
}
